<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Building_an_Interpreter/Basic_Evaluation_Functions';
    let nextLink= '/Examples/Building_an_Interpreter/Finishing_the_Test';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="using-the-context-1"
  bind:intersecting={visibleById["using-the-context-1"]}
  onIntersectChange={(visible) => setVisible("using-the-context-1", visible)}
>Using the Context</SectionComponent>
<p>Now that we’ve established how to determine basic runtime values, we can continue with the heavy work: <strong>connecting
all elements</strong>. This is where the <strong>context</strong> plays its role.</p>
<SectionComponent tag="h2" 
  id="evaluation-of-a-step-2"
  bind:intersecting={visibleById["evaluation-of-a-step-2"]}
  onIntersectChange={(visible) => setVisible("evaluation-of-a-step-2", visible)}
>Evaluation of a Step</SectionComponent>
<p>We’re working bottom-up, so the next concept to implement is <code>Step</code>.<br>
Each step has a <code>fromPage</code> and a series of answers to the questions on that page, as shown below.</p>
<Figure
imageName={'examples/Interpreter/TestB.png'}
caption={'The steps in a test'}
figureNumber={1}
/>
<p>What we need to do is:</p>
<ol>
<li>Compare the given answers for each question on the <code>fromPage</code> with the correct answers, and determine the number of correct answers.</li>
<li>Based on the answers, find the <strong>grade</strong> from the <code>fromPage</code>.</li>
<li>Using the grade, consult the <strong>page flow</strong> to find the page to switch to.</li>
<li>Return that page as the <strong>runtime value</strong> for this step.</li>
</ol>
<p>Let’s look at the implementation step by step.</p>
<h3 id="number-of-correct-answers-1">Number of Correct Answers</h3>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L113-L126

// Put the page for this step in the context
const newCtx = new InterpreterContext(ctx)

// Find the nr of correct answers and add it to the context
let nrOfCorrectAnswers = 0
for (const answer of node.answerSeries) &#123;
    const actualAnswer = main.evaluate(answer.value, newCtx)
    // Store the actual answer with the question.
    newCtx.set(answer.$question, actualAnswer)
    const expectedAnswer = main.evaluate(answer.$question.correctAnswer, newCtx)
    if (actualAnswer.equals(expectedAnswer)) &#123;
        nrOfCorrectAnswers++
    &#125;
&#125;</code></pre>
<p>Contexts can be <strong>nested</strong>. We create a new context with the parent <code>ctx</code><br>
(<code>new InterpreterContext(ctx)</code>). For every answer we evaluate the given value and
store it in the context (<code>newCtx.set(...)</code>). Recall from <code>evalQuestionReference</code>
that we retrieve a given answer by looking it up in the context:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L190-L190

const givenAnswer = ctx.find(question)</code></pre>
<p>During the loop we also count correct answers, which we’ll store in the context.</p>
<h3 id="the-grade-2">The Grade</h3>
<p>Now we have enough information to determine the grade of the <code>fromPage</code>:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L128-L129


// Find the grade for the given answers</code></pre>
<p>We use the context that contains <code>NR_OF_CORRECT_ANSWERS</code>, because page evaluation may depend on it.</p>
<h3 id="the-page-to-present-to-the-pupil-3">The Page to Present to the Pupil</h3>
<p>In the model, the page flow is defined in a <code>Flow</code> model unit.<br>
To decide which page to show next (given the current grade), the <strong>current flow</strong> must be available.
A <code>Step</code> node doesn’t know which flow to use, so we carry it in the context under the name <code>CURRENT_FLOW</code>
(the overall test evaluation stores this value).</p>
<p>From the flow we obtain the rule for the current page, then the <strong>transition</strong> for the computed grade:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L131-L147


//  Find rule for current page
const currentFlow = ctx.find("CURRENT_FLOW") as RtFlow
if (isNullOrUndefined(currentFlow)) &#123;
    return new RtError(`No flow found for page $&#123;currentPage.name&#125;`)
&#125;

const pageRule: FlowRule = currentFlow.flow.rules.find((rule) =&gt; rule.$page === currentPage)
if (isNullOrUndefined(pageRule)) &#123;
    return new RtError(`No rules found for page $&#123;currentPage.name&#125; in $&#123;currentFlow.flow.name&#125;`)
&#125;

// Find the page to which the application should switch based on the calculated grade,
// and return it as the result of evaluating this step
const transition = pageRule.transitions.find((trans) =&gt; trans.$condition === (grade as RtGrade).grade)
if (isNullOrUndefined(transition)) &#123;
    return new RtError(`No transition found for grade $&#123;grade.grade&#125; on page $&#123;currentPage.name&#125; in $&#123;currentFlow.flow.name&#125;`)</code></pre>
<p>All that’s left is to return the found page:</p>
<pre class="language-ts"><code class="language-ts">// EducationInterpreter/src/custom/interpreter/EducationInterpreter.ts#L150-L150

return new RtPage(transition.$toPage)</code></pre>


<PrevNextSection {prevLink} {nextLink} />